/**
 * Read-only zones + texts overlay for the 2D drill canvas. The editor's
 * interactive overlays handle players/cones/arrows; zones and texts aren't
 * editable here, but they must DISPLAY so the canvas matches the preview
 * (DrillMiniPreview). Uses the same coordinate mapping as every other surface.
 *
 * Mount twice: `layer="back"` (zones, behind players) and `layer="front"`
 * (texts, on top).
 */
import type { DrillZone, DrillText } from '@4cm/core/drills/types';
import {
  pitchViewBox,
  visibleRegion,
  drillNormToPitchSvg,
  type CustomPitch,
  type PitchView,
} from './pitchArea';
import type { PitchSpec } from './pitchSpecs';

interface Props {
  zones: DrillZone[];
  texts: DrillText[];
  spec: PitchSpec;
  view: PitchView;
  custom: CustomPitch | null;
  layer: 'back' | 'front';
}

export function ZonesTextsOverlay2D({ zones, texts, spec, view, custom, layer }: Props): React.JSX.Element {
  const region = visibleRegion(spec, view, custom);
  const sw = Math.min(0.3, region.width * 0.012);
  return (
    <svg
      viewBox={pitchViewBox(spec, view, custom)}
      preserveAspectRatio="xMidYMid meet"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {layer === 'back' &&
        zones.map((z) => <Zone key={z.id} zone={z} spec={spec} view={view} custom={custom} sw={sw} region={region} />)}
      {layer === 'front' &&
        texts.map((t) => <Text key={t.id} text={t} spec={spec} view={view} custom={custom} />)}
    </svg>
  );
}

function Zone({ zone, spec, view, custom, sw, region }: {
  zone: DrillZone; spec: PitchSpec; view: PitchView; custom: CustomPitch | null; sw: number; region: { length: number; width: number };
}): React.JSX.Element {
  const fill = zone.fill ?? 'rgba(46, 204, 113, 0.10)';
  const stroke = zone.stroke ?? 'rgba(46, 204, 113, 0.6)';
  const dash = zone.strokeStyle === 'dashed' ? `${sw * 3} ${sw * 2}`
             : zone.strokeStyle === 'dotted' ? `${sw * 0.6} ${sw * 1.6}`
             : undefined;
  if (zone.shape.kind === 'circle') {
    const c = drillNormToPitchSvg(zone.shape.center, spec, view, custom);
    return <circle cx={c.cx} cy={c.cy} r={zone.shape.radiusNorm * region.length} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} />;
  }
  const a = drillNormToPitchSvg(zone.shape.topLeft, spec, view, custom);
  const b = drillNormToPitchSvg(zone.shape.bottomRight, spec, view, custom);
  return (
    <rect
      x={Math.min(a.cx, b.cx)} y={Math.min(a.cy, b.cy)}
      width={Math.abs(b.cx - a.cx)} height={Math.abs(b.cy - a.cy)}
      fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} rx={sw}
    />
  );
}

function Text({ text, spec, view, custom }: {
  text: DrillText; spec: PitchSpec; view: PitchView; custom: CustomPitch | null;
}): React.JSX.Element {
  const { cx, cy } = drillNormToPitchSvg(text.position, spec, view, custom);
  const size = Math.max(0.8, (text.fontSize ?? 1.4) * 0.9);
  const color = text.color ?? 'rgba(255,255,255,0.92)';
  const rot = text.rotation ?? 0;
  const lines = text.content.split('\n');
  return (
    <text
      x={cx} y={cy}
      textAnchor="middle"
      fontSize={size}
      fontWeight={700}
      fill={color}
      transform={rot ? `rotate(${rot} ${cx} ${cy})` : undefined}
    >
      {lines.map((ln, i) => (
        <tspan key={i} x={cx} dy={i === 0 ? 0 : size * 1.15}>{ln}</tspan>
      ))}
    </text>
  );
}
